import isEqual from "lodash-es/isEqual"

export const useGoogleAssets = (
  map: Ref<google.maps.Map | undefined>,
  mainAsset: Ref<LookupAsset | undefined>,
  assets: Ref<Array<MapAsset> | undefined>,
  showMainAssetRelations: Ref<boolean | undefined>,
) => {
  const { hasScope } = useAuthStore()
  const { getRelationsByAssetId } = useAssetStore()

  const selectedAsset = ref<MapAsset | null>(null)
  const selectedAssetRelations = ref<Array<PopulatedAssetRelation>>([])

  const mainAssetRelations = ref<Array<PopulatedAssetRelation>>([])

  const allAssets = computed(() => {
    const candidateAssets: Array<MapAsset> = []

    if (assets?.value) {
      const existingIds = candidateAssets.map((asset) => asset._id) || []
      candidateAssets.push(...(assets?.value.filter((asset) => !existingIds.includes(asset._id)) ?? []))
    }

    if (mainAsset.value && !assets.value?.some((asset) => asset._id === mainAsset.value?._id)) {
      candidateAssets.push(mainAsset.value)
    }

    if (mainAsset?.value && showMainAssetRelations.value) {
      candidateAssets.push(
        ...mainAssetRelations.value
          .map((relation) => (relation.asset1._id === mainAsset?.value!._id ? relation.asset2 : relation.asset1))
          .filter((asset) => candidateAssets.every((markAsset) => asset._id !== markAsset._id)),
      )
    }

    return candidateAssets.filter((asset) => !!asset.location?.gps)
  })

  const setSelectedAsset = (asset: MapAsset | null) => (selectedAsset.value = asset)

  watch(
    () => mainAsset?.value,
    async (newVal, oldVal) => {
      if (isEqual(oldVal, newVal)) return

      if (!mainAsset?.value || !hasScope(mainAsset.value.organization, AuthScope.CAN_VIEW_ASSET_RELATIONS)) {
        return
      }

      if (showMainAssetRelations.value) {
        mainAssetRelations.value = await getRelationsByAssetId(mainAsset?.value._id)
      }
    },
    { deep: true, immediate: true },
  )

  watch(
    selectedAsset,
    async () => {
      if (!selectedAsset.value || !hasScope(selectedAsset.value.organization, AuthScope.CAN_VIEW_ASSET_RELATIONS)) {
        selectedAssetRelations.value = []
        return
      }

      selectedAssetRelations.value = await getRelationsByAssetId(selectedAsset.value._id)
    },
    { deep: true },
  )

  const initializeAssets = () => {
    if (!map.value) {
      throw new Error("Map is not initialized")
    }

    map.value.addListener("click", async () => {
      setSelectedAsset(null)
    })
  }

  return {
    initializeAssets,

    mainAssetRelations,

    selectedAsset,
    selectedAssetRelations,
    setSelectedAsset,

    allAssets,
  }
}
